import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock3, Copy, RefreshCw, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { describePickupArrival } from '../../../features/delivery/pickupArrival';
import ParcelSortTag from '../../../features/delivery/components/ParcelSortTag';
import { businessDeliveryOperationsService as api } from '../../services/businessDeliveryOperationsService';
import PickupCorrection from './PickupCorrection';

const secondsRemaining = expiresAt => {
  const value = new Date(expiresAt).getTime();
  return Number.isFinite(value) ? Math.max(0, Math.ceil((value - Date.now()) / 1000)) : 0;
};

/**
 * Releasing a trip's Parcels to the assigned Rider.
 *
 * WHAT THIS STOPPED DOING
 *
 * Staff used to scan the permanent label on every Parcel here before a challenge
 * could be issued, and the step called itself verification. It could not be.
 * The codes were printed by this same shop minutes earlier, the scanner allowed
 * typing them in, and the counter this runs on is usually a desktop PC with no
 * camera - so the realistic path was reading codes off one screen into another,
 * which proves the labels exist and nothing about where the parcels are.
 *
 * WHAT IT DOES INSTEAD
 *
 * The releasable set is the trip's own, shown here to be authorised rather than
 * re-entered. The Rider scans the physical Parcels on their phone, because the
 * Rider is the party who will be answerable for what they are carrying, and
 * they are the last person to see the parcels before they leave.
 *
 * The one-time challenge is revealed after that check, not before: it is the
 * credential that gets spent, so burning it before the set is agreed means
 * reissuing it when a Parcel turns out to be missing.
 */
// The server's own refusals (stale trip, rider not arrived, origin changed) are
// written for staff and shown as-is. A server fault or a dropped connection is
// not: it used to surface as the raw text "Internal server error".
export function releaseErrorMessage(error) {
  const status = Number(error?.response?.status || 0);
  if (!error?.response || status >= 500) {
    return 'The parcels could not be released just now. Nothing was handed over and no code is active. Try again; if it keeps failing, check your connection or contact your administrator.';
  }
  return error.response.data?.message || 'The trip or Parcel set changed. Refresh and review the current list before releasing.';
}

export default function PickupHandoff({ businessId, run, onUpdated }) {
  const [release, setRelease] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState(0);
  const parcels = useMemo(() => (run.stops || []).filter(
    stop => stop.status === 'QUEUED' && stop.assignmentStatus === 'COMMITTED',
  ), [run.stops]);
  const canRelease = run.pickupStartedAt && run.pickupArrival && run.preparationStatus === 'READY';
  const arrival = describePickupArrival(run.pickupArrival, 'business');

  useEffect(() => { setRelease(null); }, [run.id, run.manifestVersion]);
  useEffect(() => {
    if (!release?.expiresAt) return undefined;
    const update = () => {
      const next = secondsRemaining(release.expiresAt);
      setRemaining(next);
      if (next === 0 && release.qrChallengePayload) {
        setRelease(current => current ? {
          releaseId: current.releaseId,
          expiresAt: current.expiresAt,
          expired: true,
        } : current);
      }
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [release?.expiresAt, release?.qrChallengePayload]);

  const issue = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const next = await api.releasePickup(businessId, run.id, {
        expectedRunVersion: run.version,
        manifestVersion: run.manifestVersion,
      });
      if (next.secretUnavailable) {
        setRelease(null);
        setError('This request was already accepted, but its one-time code is unavailable. Reissue it to show a fresh code.');
      } else setRelease(next);
    } catch (nextError) {
      setError(releaseErrorMessage(nextError));
    } finally { setBusy(false); }
  };

  if (run.status !== 'PLANNED') return <p className="mt-4 rounded-xl bg-success-subtle p-4 text-success">This pickup handoff is closed. Open the trip receipt for its custody facts.</p>;
  const releasable = parcels.length > 0;

  return <section className="mt-4 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900" aria-labelledby="pickup-handoff-title">
    <h3 id="pickup-handoff-title" className="font-semibold">Release these Parcels to the Rider</h3>
    <p className="mt-2 text-sm leading-6 text-neutral-600 dark:text-neutral-300">Hand the Parcels below to the assigned Rider and let them check each one on their phone. When they are satisfied, release the trip to show the one-time code that completes the handoff.</p>
    {!run.pickupStartedAt && <p role="status" className="mt-3 text-sm">The Rider has not started travelling to pickup yet.</p>}
    {run.pickupStartedAt && !run.pickupArrival && <p role="status" className="mt-3 rounded-xl border border-info bg-info-subtle p-3 text-sm text-info">Challenge issuance stays locked until the assigned Rider records arrival.</p>}
    {/* Both branches used to print the same sentence, so the warning told staff
        nothing they could act on - 260 m with a poor fix read exactly like five
        kilometres away. The measured distance decides what to do about it. */}
    {arrival && <div className={`mt-3 rounded-xl p-3 text-sm ${arrival.tone === 'caution' ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'}`}><strong>{arrival.title}</strong>{arrival.evidence && <p className="mt-1 font-medium">{arrival.evidence}</p>}<p className="mt-1 leading-6">{arrival.detail}</p></div>}
    {!release ? <>
      {/* The trip's own set, shown to be authorised rather than re-entered.
          Staff scanning labels this shop printed proved only that the labels
          existed, and needed a camera the counter usually does not have. */}
      <div className="mt-4 rounded-xl bg-neutral-50 p-3 dark:bg-neutral-950">
        <div className="flex items-center justify-between gap-3 text-sm"><span className="font-semibold">Parcels on this trip</span><span>{parcels.length}</span></div>
        {releasable ? <ul className="mt-2 space-y-2">{parcels.map(stop => <li key={stop.id} className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-neutral-900">
          <span className="flex items-center gap-2 font-semibold"><ParcelSortTag labelCode={stop.parcelLabelCode} />{stop.orderReference || `Shipment ${stop.shipmentId}`}</span>
          <span className="mt-0.5 block break-all font-mono text-neutral-500 dark:text-neutral-400">{stop.parcelLabelCode || 'No label code'}</span>
        </li>)}</ul> : <p className="mt-2 text-sm text-neutral-500">No Parcels are waiting for pickup on this trip.</p>}
      </div>
      {error && <p role="alert" className="mt-3 rounded-xl bg-danger-subtle p-3 text-sm text-danger">{error}</p>}
      <button type="button" disabled={busy || !canRelease || !releasable} onClick={issue} className="business-primary-action mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-45"><ShieldCheck className="h-5 w-5" aria-hidden="true" />{busy ? 'Releasing…' : `Release ${parcels.length} ${parcels.length === 1 ? 'Parcel' : 'Parcels'} and show the code`}</button>
    </> : <div className="mt-4 rounded-2xl border border-success bg-success-subtle p-4 text-success">
      <div className="flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" /><div><p className="font-semibold">One-time handoff challenge ready</p><p className="mt-1 text-sm">Show this screen only to the assigned Rider. When they scan it, the parcels are handed over to them.</p></div></div>
      {release.expired ? <p role="alert" className="mt-4 rounded-xl border border-warning bg-warning-subtle p-3 text-sm font-semibold text-warning">Challenge expired. Its QR and fallback secret have been cleared; release the trip again to show a fresh code.</p> : <>
        <div className="mt-4 grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center"><div className="w-fit rounded-xl bg-white p-3"><QRCodeSVG value={release.qrChallengePayload} size={176} level="M" title="One-time Rider handoff challenge" /></div><div><p className="text-xs font-semibold uppercase tracking-wide">Eight-digit fallback</p><p className="mt-1 font-mono text-3xl font-bold tracking-[0.18em]">{release.fallbackCode}</p><button type="button" onClick={() => navigator.clipboard?.writeText(release.fallbackCode)} className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-xl border border-default px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><Copy className="h-4 w-4" aria-hidden="true" />Copy code</button></div></div>
        <p role="timer" className="mt-4 flex items-center gap-2 text-sm font-semibold"><Clock3 className="h-4 w-4" aria-hidden="true" />Expires in {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}</p>
      </>}
      <button type="button" disabled={busy} onClick={() => { setRelease(null); }} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-default px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><RefreshCw className="h-4 w-4" aria-hidden="true" />Show a fresh code</button>
    </div>}
    <details className="mt-4 border-t border-neutral-200 pt-2 dark:border-neutral-700"><summary className="min-h-11 cursor-pointer py-3 text-sm font-medium">A Parcel is missing or unavailable</summary><PickupCorrection businessId={businessId} run={run} onUpdated={onUpdated} /></details>
  </section>;
}
